import React, { Component } from 'react';
import { Row, Col, Form, Input, Button, Alert } from 'antd';
import * as HttpStatus from 'http-status-codes';
import Card from '../../Card/Card';
import Modal from '../../Modal/Modal';
import Login from '../Login';
import { MSG_ERROR } from '../../../constants';
import {
  postSendAuthenticatorRecoveryEmail,
  postConfirmAuthenticatorRecovery,
} from '../../../services/mfaAuthenticatorService';
import { parseHttpError } from '../../../utils/httpError';

const FormItem = Form.Item;

const MSG_ALERT_SEND_SUCCESS =
  'If this email address is registered and uses an authenticator app, you will receive an email with a link to turn off authenticator sign-in.';
const MSG_ALERT_SEND_UNAUTHORIZED = `Sorry, we couldn't find an account with the email`;
const MSG_ALERT_CONFIRM_SUCCESS =
  'Authenticator sign-in has been turned off for your account. You can log in with email verification next time.';
const MSG_ALERT_CONFIRM_FAIL =
  'We could not complete this request. The link may be invalid or expired. Please request a new email from the authenticator recovery page.';

const initialState = {
  userId: null,
  token: null,
  valid: false,
  alertType: null,
  alertMessage: null,
  sending: false,
  confirming: false,
};

class AuthenticatorRecoveryForm extends Component {
  state = { ...initialState };

  componentDidMount() {
    const parsedUrl = new URL(window.location);
    const searchParams = new URLSearchParams(parsedUrl.search);
    const userId = searchParams.get('id');
    // Trial: decode via URLSearchParams, then re-encode for API (similar idea to EmailInvitation verify).
    const decodedToken = searchParams.get('token');
    const token = decodedToken != null ? encodeURIComponent(decodedToken) : null;

    const valid = Boolean(userId && token);
    if (valid) {
      this.setState({ userId, token, valid, confirming: true }, () => {
        this.confirmFromLink();
      });
    } else {
      this.setState({ userId, token, valid });
    }
  }

  confirmFromLink = async () => {
    const { userId, token } = this.state;
    if (!userId || !token) return;
    try {
      await postConfirmAuthenticatorRecovery({ userId, token });
      this.setState({
        alertType: 'success',
        alertMessage: MSG_ALERT_CONFIRM_SUCCESS,
        confirming: false,
      });
    } catch (err) {
      const { status } = parseHttpError(err);
      let message = MSG_ALERT_CONFIRM_FAIL;
      if (status === HttpStatus.IM_A_TEAPOT) {
        message = 'This link is invalid or has expired. Please go to authenticator recovery and request a new email.';
      } else if (status === HttpStatus.BAD_REQUEST || status === HttpStatus.UNAUTHORIZED) {
        message = MSG_ALERT_CONFIRM_FAIL;
      } else if (status !== undefined) {
        message = MSG_ERROR;
      }
      this.setState({
        alertType: 'error',
        alertMessage: message,
        confirming: false,
      });
    }
  };

  onSendRecoveryEmail = (e) => {
    e.preventDefault();
    const { form } = this.props;
    form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;
      const email = values.recoveryEmail;
      this.setState({ sending: true });
      try {
        await postSendAuthenticatorRecoveryEmail({ email });
        this.setState({ alertType: 'success', alertMessage: MSG_ALERT_SEND_SUCCESS });
      } catch (error) {
        const { status } = parseHttpError(error);
        if (status === HttpStatus.UNAUTHORIZED) {
          this.setState({ alertType: 'error', alertMessage: `${MSG_ALERT_SEND_UNAUTHORIZED} ${email}.` });
        } else {
          this.setState({ alertType: 'error', alertMessage: MSG_ERROR });
        }
      }
      this.setState({ sending: false });
    });
  };

  redirectToCleanRecovery = () => {
    const { history } = this.props;
    this.setState(initialState, () => {
      history.replace('/authenticator-recovery');
    });
  };

  render() {
    const { form } = this.props;
    const { getFieldDecorator } = form;
    const { valid, alertType, alertMessage, sending, confirming } = this.state;

    if (valid) {
      return (
        <div className="reset-password">
          <Card title="Authenticator recovery">
            <Row gutter={20} className="reset-password-inner">
              <Col span={24}>
                {confirming && !alertMessage && (
                  <Alert type="info" message="Completing your request…" className="reset-password-alert" />
                )}
                {alertMessage && <Alert type={alertType} message={alertMessage} className="reset-password-alert" />}
              </Col>
              <Col span={24}>
                {alertType === 'error' ? (
                  <Button type="primary" onClick={this.redirectToCleanRecovery} className="reset-password-btn">
                    Request a new email
                  </Button>
                ) : null}
                {alertType === 'success' ? (
                  <Modal
                    toggle={(show) => (
                      <Button type="primary" onClick={show} className="reset-password-btn">
                        Log in
                      </Button>
                    )}
                  >
                    {(hide) => <Login close={hide} />}
                  </Modal>
                ) : null}
              </Col>
            </Row>
          </Card>
        </div>
      );
    }

    return (
      <div className="reset-password">
        <Card title="Authenticator recovery">
          <Row gutter={20} className="reset-password-inner">
            <Col span={24}>
              {alertMessage && <Alert type={alertType} message={alertMessage} className="reset-password-alert" />}
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <p>
                If you cannot use Microsoft Authenticator or Google Authenticator on your phone, we can email you a
                secure link to turn off authenticator sign-in. After that, you can log in using email verification
                instead.
              </p>
              <p>
                Enter the email address for your ContinuityCoach account. For security, we only send a link if the
                account exists and has authenticator enabled.
              </p>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <Form onSubmit={this.onSendRecoveryEmail}>
                <FormItem hasFeedback>
                  {getFieldDecorator('recoveryEmail', {
                    validateTrigger: 'onBlur',
                    rules: [
                      { type: 'email', message: 'Please input a valid email.' },
                      { required: true, message: 'Please input your email.' },
                    ],
                  })(<Input placeholder="Email" spellCheck={false} disabled={sending} />)}
                </FormItem>
                <FormItem>
                  <Button type="primary" htmlType="submit" loading={sending} className="reset-password-btn">
                    {sending ? 'Sending…' : 'Send recovery email'}
                  </Button>
                </FormItem>
              </Form>
              <p className="authenticator-recovery-back">
                <a href="/">Back to login</a>
              </p>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
}

export default Form.create()(AuthenticatorRecoveryForm);
